import CloseIcon from '@mui/icons-material/Close'
import { type StateFileUpload } from 'types/fileUpload'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { getFileSizeKb, getFileExtension } from 'utils/util'
import Image from 'assets/image-black.svg?react'

import { useEffect, useState, type ElementType } from 'react'

const resolveIconComponent = (iconSource: unknown): ElementType => {
  if (iconSource && typeof iconSource === 'object' && 'default' in iconSource) {
    return (iconSource as { default: ElementType }).default
  }

  return iconSource as ElementType
}

export const UploadingFile = ({
  file,
  fileFormatsAllowed,
  maxSizeAllowed,
  removeFile,
  toggleStateFilesUpload,
}: {
  file: File
  fileFormatsAllowed: string
  maxSizeAllowed: number
  removeFile: (file: File) => void
  toggleStateFilesUpload: (value: StateFileUpload) => void
}) => {
  const CloseIconComponent = resolveIconComponent(CloseIcon)
  const ImageComponent = resolveIconComponent(Image)
  const [validatedFile, setValidatedFile] = useState('')
  useEffect(() => {
    const enumInvalid = 2
    const enumValid = 1
    let validationResult = ''

    // Validate format
    const actualFormat = getFileExtension(file.name).toLowerCase()
    const allowedFormats = fileFormatsAllowed
      .split(',')
      .map((format) => {
        const normalizedFormat = format.trim().toLowerCase().replace('.', '')
        if (normalizedFormat.includes('/')) {
          return normalizedFormat.split('/').pop() ?? ''
        }

        return normalizedFormat
      })
      .filter(Boolean)

    validationResult = !allowedFormats.includes(actualFormat)
      ? 'Failed Wrong file format'
      : ''

    // Validate size and max size
    const actualSizeKb = getFileSizeKb(file.size)
    if (actualSizeKb === 0 && validationResult === '') {
      validationResult = 'Failed File size is 0'
    }
    if (actualSizeKb > maxSizeAllowed && validationResult === '') {
      validationResult = 'Failed File too large'
    }

    setValidatedFile(validationResult)
    toggleStateFilesUpload(validationResult !== '' ? enumInvalid : enumValid)
  }, [])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', paddingTop: '2%' }}>
      <Box className="justify-items-start w-[15%] md:w-[12%] lg:w-[48px]">
        {ImageComponent ? <ImageComponent height={32} width={32} /> : null}
      </Box>
      <Box sx={{ width: '80%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: { xs: '75%', lg: '40%', xl: '28%' },
          }}
        >
          <Typography variant="body-3">{file.name}</Typography>
          {validatedFile === '' ? (
            <Typography variant="body-3">
              {getFileSizeKb(file.size)} kb
            </Typography>
          ) : (
            <Typography variant="body-3" className={`!text-[#CC2027]`}>
              {validatedFile}
            </Typography>
          )}
        </Box>
      </Box>
      <Box className="flex justify-end w-[10%] xl:w-[15%]">
        <Button
          className="flex justify-end"
          style={{
            justifyContent: 'flex-end',
            color: 'rgba(41, 43, 38, 0.6)',
            minWidth: 'auto',
          }}
          type="button"
          onClick={() => {
            removeFile(file)
          }}
          endIcon={CloseIconComponent ? <CloseIconComponent /> : null}
        />
      </Box>
    </Box>
  )
}
